import { Vec3 } from '../../engine/math/vec3';
import { clamp, wrapAngle, DEG, easeInQuad } from '../../engine/math/easing';
import { capsule, cylinder, box, cone, sphere, combine } from '../../engine/geo/primitives';
import { translate, rotateZ, rotateX, setEmissive } from '../../engine/geo/mesh';
import type { Color } from '../../engine/geo/mesh';
import type { Ctx } from '../Ctx';
import type { Player } from '../player/Player';
import type { Particles } from '../fx/Particles';
import { Beast } from './Beast';
import type { IronwoodHandles } from '../biomes/Ironwood';

const BLACK: Color = [0.08, 0.07, 0.09];

/**
 * Ironwood Stag (§5): patrols maze corridors, sniffs at corners, and charges in a straight line at 14 m/s
 * until it hits a trunk cluster, then staggers for 2 s. 1 heart + 4 m knockback on contact. HP 4.
 */
export class Stag extends Beast {
  private readonly vel = new Vec3();
  private heading = 0;
  private path: Vec3[] = [];
  private pathI = 0;
  private patrolI = 0;
  private sniffT = 0;
  private chargeDist = 0;
  private hitDone = false;
  private legPhase = 0;
  private readonly head;
  private readonly legs;
  private readonly body;
  particles: Particles | null = null;

  constructor(ctx: Ctx, pos: Vec3, private readonly maze: IronwoodHandles) {
    super(ctx, 'stag', pos, 4, [0.35, 0.1, 0.12], 75);
    // barrel body (horizontal capsule at 1.4 m), long legs, boxy head with cone antlers, red eyes
    const barrel = rotateZ(capsule(0.55, 1.3, 10, 3, BLACK), Math.PI / 2);
    this.body = this.addPart('stag.body', barrel, this.root, new Vec3(0, 1.45, 0), 0.9);
    const legMesh = () => translate(cylinder(0.08, 0.1, 1.2, 6, BLACK), 0, -0.6, 0);
    this.legs = [
      this.addPart('stag.leg0', legMesh(), this.root, new Vec3(0.3, 1.25, 0.55), 0.2),
      this.addPart('stag.leg1', legMesh(), this.root, new Vec3(-0.3, 1.25, 0.55), 0.2),
      this.addPart('stag.leg2', legMesh(), this.root, new Vec3(0.3, 1.25, -0.55), 0.2),
      this.addPart('stag.leg3', legMesh(), this.root, new Vec3(-0.3, 1.25, -0.55), 0.2),
    ];
    const headMesh = combine(
      box(0.42, 0.42, 0.7, BLACK),
      translate(box(0.26, 0.24, 0.3, BLACK), 0, -0.08, 0.45),
      setEmissive(translate(sphere(0.06, 6, 4, [1, 0.2, 0.15]), 0.16, 0.08, 0.3), [1, 0.15, 0.1], 1.4),
      setEmissive(translate(sphere(0.06, 6, 4, [1, 0.2, 0.15]), -0.16, 0.08, 0.3), [1, 0.15, 0.1], 1.4),
      translate(rotateX(cone(0.07, 1.0, 5, BLACK), -0.5), 0.22, 0.6, -0.1),
      translate(rotateX(cone(0.07, 1.0, 5, BLACK), -0.5), -0.22, 0.6, -0.1),
      translate(rotateX(cone(0.05, 0.6, 5, BLACK), -1.1), 0.34, 0.5, 0.05),
      translate(rotateX(cone(0.05, 0.6, 5, BLACK), -1.1), -0.34, 0.5, 0.05),
    );
    this.head = this.addPart('stag.head', headMesh, this.root, new Vec3(0, 2.1, 1.05), 0.5);
    for (const p of [this.body]) {
      const crack = combine(
        setEmissive(translate(box(0.05, 0.9, 0.05, [0.5, 0.1, 0.1]), 0, 0.1, 0.4), [0.6, 0.1, 0.1], 0.8),
        setEmissive(translate(box(0.05, 0.7, 0.05, [0.5, 0.1, 0.1]), 0.1, -0.1, -0.3), [0.6, 0.1, 0.1], 0.8),
      );
      this.addCrack('stag.crack', crack, p.t);
    }
    this.newPatrolPath();
  }

  protected onKnockback(dir: Vec3): void { this.vel.addScaled(dir, 2); }

  private newPatrolPath(): void {
    const from = this.maze.cellOf(this.position);
    this.patrolI = (this.patrolI + 1) % this.maze.patrol.length;
    const to = this.maze.cellOf(this.maze.patrol[this.patrolI]);
    this.path = this.maze.path(from, to);
    this.pathI = 1;
  }

  private groundY(x: number, z: number): number { return this.ctx.statics.ground(x, 50, z, 100).y; }

  update(dt: number, player: Player): void {
    this.tick(dt);
    if (!this.alive) return;
    const st = this.ctx.statics;
    const p = this.position;
    const engaged = this.updateAggro(player, dt);
    const toP = _v.copy(player.position).sub(p); toP.y = 0;
    const dist = toP.length();
    const sp = this.speedMul;

    switch (this.state) {
      case 'idle':
      case 'alert': {
        // follow the current path; when engaged, path toward the player's cell instead
        if (engaged && this.stateT > 0.5) {
          const target = this.maze.cellOf(player.position);
          const from = this.maze.cellOf(p);
          const np = this.maze.path(from, target);
          if (np.length > 1) { this.path = np; this.pathI = 1; }
          this.stateT = 0;
        }
        if (this.sniffT > 0) {
          this.sniffT -= dt;
          this.vel.scale(1 - 6 * dt);
        } else if (this.pathI < this.path.length) {
          const wp = this.path[this.pathI];
          const d = _d.copy(wp).sub(p); d.y = 0;
          const l = d.length();
          if (l < 0.8) {
            this.pathI++;
            if (this.pathI >= this.path.length) { this.sniffT = 1.2; this.newPatrolPath(); }
            else this.sniffT = engaged ? 0.15 : 0.9; // stop and sniff at corners
          } else {
            const want = Math.atan2(d.x, d.z);
            this.heading += clamp(wrapAngle(want - this.heading), -4 * dt, 4 * dt);
            const speed = (engaged ? 4.2 : 2.4) * sp;
            this.vel.x = Math.sin(this.heading) * speed; this.vel.z = Math.cos(this.heading) * speed;
          }
        } else this.newPatrolPath();
        this.state = engaged ? 'alert' : 'idle';
        // charge when the player is down the corridor it faces (only turns at corners)
        if (engaged && dist < 16 && dist > 3 && this.canSee(player)) {
          const angle = Math.abs(wrapAngle(Math.atan2(toP.x, toP.z) - this.heading));
          if (angle < 14 * DEG) {
            this.setState('telegraph');
            this.ctx.sfx.telegraph('stag', p, 0.7);
          }
        }
        break;
      }
      case 'telegraph': {
        this.vel.scale(1 - 8 * dt);
        if (this.stateT >= 0.7) { this.setState('attack'); this.chargeDist = 0; this.hitDone = false; }
        break;
      }
      case 'attack': {
        const speed = 14 * sp;
        this.vel.x = Math.sin(this.heading) * speed; this.vel.z = Math.cos(this.heading) * speed;
        this.chargeDist += speed * dt;
        if (!this.hitDone) {
          const hc = _d.copy(p).addScaled(_f.set(Math.sin(this.heading), 0, Math.cos(this.heading)), 1.2); hc.y = player.position.y + 0.8;
          if (this.tryHitPlayer(player, hc, 1.2, 4)) { this.hitDone = true; this.ctx.fx.shake(0.35, 0.35); }
        }
        if (this.chargeDist > 45) this.setState('stagger');
        break;
      }
      case 'stagger': {
        this.vel.scale(1 - 10 * dt);
        if (this.particles && (this.stateT * 60 | 0) % 6 === 0) {
          const a = this.stateT * 6;
          this.particles.mote(_d.set(p.x + Math.cos(a) * 0.9, p.y + 2.7, p.z + Math.sin(a) * 0.9), _f.set(0, 0.2, 0), 0.4, 0.08, [0.9, 0.9, 1], 0.6, 0);
        }
        if (this.stateT >= 2.0) { this.setState('alert'); this.newPatrolPath(); }
        break;
      }
      default: break;
    }

    // integrate with wall collision; a charge that hits a wall becomes a stagger
    p.x += this.vel.x * dt; p.z += this.vel.z * dt;
    const pushed = st.resolveCapsule(p, 0.7, 2.2, 0.4, _push);
    if (pushed && this.state === 'attack') {
      this.setState('stagger');
      this.ctx.sfx.stagStagger(p);
      this.ctx.fx.shake(0.25, 0.3);
      this.particles?.burst(_d.copy(p).addScaled(_f.set(Math.sin(this.heading), 0, Math.cos(this.heading)), 1.5).add(_up), 8, [0.3, 0.25, 0.2], 3, 0.15, 0.6, 0);
    } else if (pushed && this.state !== 'stagger') {
      // bounced off a trunk while patrolling: re-path
      this.newPatrolPath();
    }
    p.y = this.groundY(p.x, p.z);
    this.root.rotation.setYaw(this.heading);

    // animation: rear during telegraph, legs gallop, head bob when sniffing, stagger wobble
    const speed = Math.hypot(this.vel.x, this.vel.z);
    this.legPhase += speed * 1.6 * dt;
    for (let i = 0; i < 4; i++) {
      const s = Math.sin(this.legPhase + (i % 2 === 0 ? 0 : Math.PI) + (i < 2 ? 0 : 0.6)) * clamp(speed / 6, 0, 1) * 0.6;
      this.legs[i].t.rotation.setAxisAngle(_x, s);
    }
    if (this.state === 'telegraph') {
      const k = easeInQuad(clamp(this.stateT / 0.7, 0, 1));
      this.body.t.rotation.setAxisAngle(_x, -k * 0.7);
      this.body.t.position.y = 1.45 + k * 0.5;
      this.head.t.position.set(0, 2.1 + k * 0.9, 1.05 - k * 0.4);
      this.body.t.scale.set(1.08 - k * 0.12, 1 + k * 0.15, 1);
    } else if (this.state === 'stagger') {
      this.body.t.rotation.setEuler(0, 0, Math.sin(this.stateT * 9) * 0.12);
      this.body.t.position.y = 1.45; this.body.t.scale.set(1, 1, 1);
      this.head.t.position.set(Math.sin(this.stateT * 7) * 0.15, 1.9, 1.05);
    } else {
      this.body.t.rotation.setAxisAngle(_x, 0);
      this.body.t.position.y = 1.45 + Math.abs(Math.sin(this.legPhase)) * 0.06 * clamp(speed / 6, 0, 1);
      this.body.t.scale.set(1, 1, 1);
      const sniff = this.sniffT > 0 ? Math.sin(this.ctx.time * 9) * 0.12 : 0;
      this.head.t.position.set(0, 2.1 + sniff - (this.sniffT > 0 ? 0.35 : 0), 1.05);
      this.head.t.rotation.setAxisAngle(_x, this.sniffT > 0 ? 0.35 : 0.1);
    }
  }
}
const _v = new Vec3(), _d = new Vec3(), _f = new Vec3(), _push = new Vec3(), _x = new Vec3(1, 0, 0), _up = new Vec3(0, 1.4, 0);
